<script
  setup
  lang="ts"
  generic="
    ListingFilter extends {
      code: string;
      min?: number;
      max?: number;
      label: string;
    }
  "
>
import { useCmsTranslations } from "@shopware-pwa/composables-next";
import type { Schemas } from "#shopware";
import { onMounted, reactive, ref, watch } from "vue";
import { defu } from "defu";
import { onClickOutside, useDebounceFn } from "@vueuse/core";

const emits = defineEmits<{
  (e: "select-value", value: { code: string; value: unknown }): void;
}>();

const props = defineProps<{
  filter: ListingFilter;
  selectedFilters: Schemas["ProductListingResult"]["currentFilters"];
}>();

const { filter, selectedFilters } = props;

type Translations = {
  listing: {
    min: string;
    max: string;
  };
};
let translations: Translations = {
  listing: {
    min: "Min",
    max: "Max",
  },
};
translations = defu(useCmsTranslations(), translations) as Translations;

const prices = reactive<{ min: number; max: number }>({
  min: 0,
  max: 0,
});

prices.min = Math.floor(selectedFilters.price.min ?? filter?.min ?? 0);
prices.max = Math.floor(selectedFilters.price.max ?? filter?.max ?? 0);

// watch(
//   () => props.selectedFilters,
//   (newFilter, oldFilter) => {
//     if (newFilter.price.min !== oldFilter.price.min) {
//       prices.min = parseFloat(newFilter.price.min);
//       if (prices.min === 0) {
//         prices.min = parseFloat(filter.min);
//       }
//     }
//     if (newFilter.price.max !== oldFilter.price.max) {
//       prices.max = parseFloat(newFilter.price.max);
//       if (prices.max === 0) {
//         prices.max = parseFloat(filter.max);
//       }
//     }
//   }
// );

const dropdownElement = ref(null);

function onMinPriceChange(newPrice: number, oldPrice: number) {
  if (newPrice === oldPrice || oldPrice === 0) return;
  emits("select-value", {
    code: "min-price",
    value: newPrice,
  });
}
const debounceMinPriceUpdate = useDebounceFn(onMinPriceChange, 500);
watch(() => prices.min, debounceMinPriceUpdate);

function onMaxPriceChange(newPrice: number, oldPrice: number) {
  if (newPrice === oldPrice || oldPrice === 0) return;
  emits("select-value", {
    code: "max-price",
    value: newPrice,
  });
}
const debounceMaxPriceUpdate = useDebounceFn(onMaxPriceChange, 500);
watch(() => prices.max, debounceMaxPriceUpdate);

if (prices.min === 0) {
  prices.min = parseFloat(filter.min);
}

if (prices.max === 0) {
  prices.max = parseFloat(filter.max);
}

const isFilterVisible = ref(true);

function toggle() {
  isFilterVisible.value = !isFilterVisible.value;
}

if (
  selectedFilters.price.min > filter?.min &&
  selectedFilters.price.max < filter?.max
) {
  toggle();
}
</script>

<template>
  <div class="filter-price-element">
    <button
      class="accordion-header w-full text-left py-4 font-bold flex justify-between items-center cursor-pointer bg-transparent"
      @click="toggle"
    >
      <span class="text-base font-bold text-dark-bg">{{ $t("listing.price") }}</span>
      <svg
        id="icon-price"
        xmlns="http://www.w3.org/2000/svg"
        class="h-[16px] w-[16px] transform transition-transform duration-200"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
        :class="[!isFilterVisible ? 'rotate-0' : 'rotate-180']"
      >
        <path
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="2"
          d="M19 9l-7 7-7-7"
        ></path>
      </svg>
    </button>

    <transition name="fade" mode="out-in">
      <div
        class="accordion-body pb-4"
        :class="[!isFilterVisible ? 'hidden' : '']"
      >
        <SharedRangeSlider
          :min="props.filter.min"
          :max="props.filter.max"
          v-model:min-value="prices.min"
          v-model:max-value="prices.max"
        />
      </div>
    </transition>
  </div>
</template>
<style scoped>
.fade-enter-active,
.fade-leave-active {
  transition: all 0.2s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
